import { uniq } from 'lodash';
import { DomainCoreGeneration } from '../libraries/DomainCoreGeneration';
import OrderHelperObject from './OrderHelperObject';
import { parseWildcardDates } from '../helpers/dateHelpers';
import { reduceRight, reduceRightWithParent } from './OrderHelpers';
import { extractValueFromOrderTags } from './OrderTags';
import getLuxonImpl from '../plugins/commonLibraries/timezone/timeboxImpl';
import { getCustomDiscountTags, isRuleActive, shouldRuleBeAppliedToShop } from '../priceRules/priceRulesHelpers';
const getIdAsName = (guid, prefix) => {
    return prefix + guid.substring(guid.length - 8).replace(/0/g, '');
};
const createOrderLineFromGroup = (p, g) => {
    const group = p.getGroup(g.groupId);
    const orderLine = {
        name: group.name || group.options.description || getIdAsName(group.id, 'Group'),
        type: 'Aggregation',
        options: group.options || [],
        subLines: [],
        generation: DomainCoreGeneration.Generation4,
    };
    return orderLine;
};
const createOrderLineFromProduct = (product, rootProductId, quantity, depth) => {
    const computeOrderLinePrice = () => {
        if (product.priceStructure && product.priceStructure.priceInGroup && depth > 0) {
            return product.priceStructure.priceInGroup;
        }
        else if (product.price) {
            return product.price;
        }
    };
    const orderLinePrice = computeOrderLinePrice();
    const orderLineQuantity = quantity || undefined;
    const computeTotalPrice = () => {
        if (orderLinePrice) {
            return orderLinePrice * (orderLineQuantity !== undefined ? orderLineQuantity : 1);
        }
        return 0;
    };
    const orderLine = {
        type: 'Product',
        productId: product.id,
        name: product.name || getIdAsName(product.id, 'product'),
        subLines: [],
        quantity: orderLineQuantity,
        aggregatedQuantity: undefined,
        options: product.options
            ? Object.assign(Object.assign({}, product.options), { foreignIds: product.foreignIds, foreignNames: product.foreignNames, overrides: product.overrides, priceStructure: product.priceStructure }) : undefined,
        unitType: (product.unit || undefined),
        accounting: product.options.accounting || undefined,
        discount: product.discount,
        applicableDiscounts: product.applicableDiscounts,
        price: orderLinePrice,
        totalPrice: computeTotalPrice(),
        rootProductId: rootProductId,
        totalPriceAccum: 0,
        productSelectionId: '',
        generation: DomainCoreGeneration.Generation4,
    };
    return orderLine;
};
const createOrderLineFromConfig = (config, configSelectionNode) => {
    const _options = config.options.options;
    const selectedValue = Array.isArray(configSelectionNode.selectedValue)
        ? configSelectionNode.selectedValue
        : configSelectionNode.selectedValue !== undefined
            ? [configSelectionNode.selectedValue]
            : [];
    const orderLine = {
        name: config.name || getIdAsName(config.id, 'config'),
        type: 'Config',
        options: config.options,
        selectedOptions: _options
            .filter((opt, index) => selectedValue.indexOf(index) !== -1)
            .map((op) => new Object(Object.assign({}, op))),
        unselectedOptions: _options.reduce((acc, opt, index) => {
            if (selectedValue.indexOf(index) === -1) {
                acc.push(new Object(Object.assign(Object.assign({}, opt), { optionIndex: index, configId: config.id })));
            }
            return acc;
        }, []),
        subLines: [],
        generation: DomainCoreGeneration.Generation4,
    };
    return orderLine;
};
const recurse = (productCatalog, orderLineArray, productSelectionNode, depth, rootProductId) => {
    const product = productCatalog.getProduct(productSelectionNode.productId, true);
    const orderLine = createOrderLineFromProduct(product, rootProductId, productSelectionNode.quantity, depth);
    orderLine.subLines = [];
    if (productSelectionNode.configurations) {
        productSelectionNode.configurations.forEach((configSelectionNode) => {
            const config = product.getConfig(configSelectionNode.configurationId);
            const configLine = createOrderLineFromConfig(config, configSelectionNode);
            if (orderLine.type === 'Product') {
                orderLine.subLines.push(configLine);
            }
        });
    }
    if (productSelectionNode.groups) {
        productSelectionNode.groups.forEach((g) => {
            const groupLine = createOrderLineFromGroup(product, g);
            groupLine.subLines = [];
            g.selectedProducts.forEach((p) => {
                const productSelectionNodeInner = (typeof p === 'string'
                    ? { productId: p }
                    : p);
                recurse(productCatalog, groupLine.subLines, productSelectionNodeInner, depth + 1, rootProductId);
            });
            groupLine.generation = DomainCoreGeneration.Generation4;
            orderLine.subLines.push(groupLine);
        });
    }
    orderLine.generation = DomainCoreGeneration.Generation4;
    orderLineArray.push(orderLine);
};
const helperFunctions = {
    getIdAsName,
    recurse,
    createOrderLineFromGroup,
    createOrderLineFromProduct,
    createOrderLineFromConfig,
};
const mapToOrderLinesG4 = (productCatalog, productSelection, orderTags = []) => {
    const { priceRules, selectedShopId, currentDate } = productCatalog;
    const filteredByShop = priceRules.filter((rule) => {
        if (selectedShopId === null) {
            return true;
        }
        return shouldRuleBeAppliedToShop(rule.shopPattern, selectedShopId);
    });
    const filteredRules = filteredByShop.filter((rule) => {
        const { Formats: TimeBoxFormats, timebox: TimeBox } = getLuxonImpl();
        if (Array.isArray(rule.orderingDatePattern)) {
            const shopCurrentDate = TimeBox
                .convertRelativeStringDate(currentDate, productCatalog.userTimeZone, productCatalog.pickupTimeZone)
                .format(TimeBoxFormats.YYYY_MM_DD)
                .exec();
            const limitDates = uniq(rule.orderingDatePattern.reduce((carry, dateFilterItem) => {
                return [...carry, ...parseWildcardDates(dateFilterItem, new Date(currentDate))];
            }, []));
            return limitDates.indexOf(shopCurrentDate) !== -1;
        }
        else if (Array.isArray(rule.weekDay)) {
            return isRuleActive(rule, productCatalog.pickupTimeZone);
        }
        else {
            return false;
        }
    });
    const { global: globalRules } = filteredRules.reduce((acc, curr) => {
        if ('productSpecific' in curr && curr.productSpecific === false) {
            const c = curr;
            return Object.assign(Object.assign({}, acc), { global: [...acc.global, c] });
        }
        const c = curr;
        return Object.assign(Object.assign({}, acc), { specific: [...acc.specific, c] });
    }, { specific: [], global: [] });
    const matchGlobalDiscount = (rule) => {
        const membership = extractValueFromOrderTags('Membership', orderTags);
        return membership && rule.options && rule.options.label && rule.options.label.includes(membership);
    };
    const computedGlobalPriceRules = globalRules.filter(matchGlobalDiscount);
    const orderLines = [];
    productSelection.forEach((node) => helperFunctions.recurse(productCatalog, orderLines, node, 0, node.productId));
    reduceRight(orderLines, (acc, curr) => {
        const includedPrice = curr.options &&
            curr.options.foreignIds &&
            curr.options.foreignIds.onlinePosRules &&
            curr.options.foreignIds.onlinePosRules.cart &&
            curr.options.foreignIds.onlinePosRules.cart.find((c) => c.inclPrice);
        if (includedPrice && curr.type === 'Product') {
            curr.price += includedPrice.inclPrice;
        }
        return acc;
    }, {});
    const totalPantsCost = reduceRightWithParent(orderLines, (acc, curr, parent) => {
        const includedPrice = curr.options &&
            curr.options.foreignIds &&
            curr.options.foreignIds.onlinePosRules &&
            curr.options.foreignIds.onlinePosRules.cart &&
            curr.options.foreignIds.onlinePosRules.cart.find((c) => c.inclPrice);
        if (includedPrice && curr.type === 'Product') {
            if (curr.quantity) {
                acc += curr.quantity * includedPrice.inclPrice;
            }
            else if (parent !== null) {
                acc += includedPrice.inclPrice;
            }
        }
        return acc;
    }, 0, null);
    const generateDiscountLine = () => {
        const totalPrice = reduceRightWithParent(orderLines, (acc, curr, parent) => {
            if (curr.type === 'Product') {
                if (parent !== null) {
                    return acc + curr.totalPrice;
                }
                const price = typeof curr.price === 'number' ? acc + curr.totalPrice : acc * (curr.quantity || 1);
                return price;
            }
            return acc;
        }, 0, null);
        const bestDiscountP = computedGlobalPriceRules.reduce((acc, curr) => {
            const tags = [];
            const discountTags = getCustomDiscountTags(curr.options);
            if (discountTags.length > 0) {
                tags.push(...discountTags);
            }
            const hasFreeMeal = extractValueFromOrderTags('FirstMeal', orderTags) === 'true';
            const freeMealBonus = 100;
            const discountablePrice = Math.min(totalPrice, curr.options.discountMax + (hasFreeMeal ? freeMealBonus : 0));
            if (discountablePrice !== totalPrice) {
                tags.push('DiscountCapped');
            }
            if (hasFreeMeal) {
                tags.push('FreeMeal');
            }
            const calcualteTotalDiscount = (discountablePrice, hasFreeMeal, freeMealBonus, discountPct) => {
                if (hasFreeMeal) {
                    return (discountablePrice - freeMealBonus) * discountPct + freeMealBonus;
                }
                return discountablePrice * discountPct;
            };
            const totalDiscount = calcualteTotalDiscount(discountablePrice, hasFreeMeal, freeMealBonus, curr.options.discountPct);
            if (acc.totalDiscount < totalDiscount) {
                const totalAppliedDiscount = Math.min(totalDiscount, totalPrice + totalPantsCost);
                return { rule: curr, totalDiscount: totalAppliedDiscount, tags };
            }
            return acc;
        }, { rule: null, totalDiscount: 0, tags: [] });
        const discountLine = {
            name: 'Total Rabat',
            subLines: [],
            type: 'Discount',
            tags: bestDiscountP.tags,
            totalPrice: -1 * bestDiscountP.totalDiscount,
            generation: DomainCoreGeneration.Generation4,
        };
        return discountLine;
    };
    const discountLine = generateDiscountLine();
    const orderLinesWithDiscount = discountLine.type === 'Discount' && discountLine.totalPrice !== 0 ? orderLines.concat([discountLine]) : orderLines;
    const orderLinesEffectivePrices = OrderHelperObject._applyDiscountsForOrderLines(orderLinesWithDiscount);
    OrderHelperObject._accumulatePrices(orderLinesEffectivePrices);
    OrderHelperObject._fixPricesForProductsWithFreeAdditions(orderLinesEffectivePrices);
    return orderLinesEffectivePrices;
};
const productSelection = {
    mapToOrderLinesG4,
};
export default productSelection;
